import { Clazz } from './clazz'
import { Student } from './student'
import { IQueryPagination } from './query'
import { ITeacherCreateForm } from './teacherAuth'

export interface ICourseLiveCoach {
  id: string
  name: string
  avatar: string
}

export enum CourseLiveStatus {
  Open = 'being_open',
  InProgress = 'processing',
  Ended = 'ending',
}

export interface ICourseContentItem {
  item_type: string
  item_title: string
  exercise_strength: string
  phone_style: string | null
  img_url: string | null
  drill_id: string
}

export interface ICourseDetail {
  energy: number
  items_count: number
  tags: string[]
  video_url: string
  class_list: Clazz[]
  course_template: string

  items: ICourseContentItem[]
}

export interface ICourseLive {
  id: string
  title: string
  intro: string
  start_time: string
  end_time: string
  duration: number
  live_status: CourseLiveStatus
  coach: ICourseLiveCoach
  marked: boolean
  difficult_level: string
  sponsor: string
  description: string
  detail?: ICourseDetail
}

export interface ICourseLiveCountPerDay {
  date: string
  count: number
}
export interface ICourseStatisticsStudentInfo {
  user: Student
  entered: boolean
  complete_status: string
  score_grade: number
  class_vo: Clazz
}

export interface IDrillProgressUserInfo {
  user_id: string | null
  head_img_url: string
}
export interface ICourseLiveDrillProgressInfo {
  drill_id: string
  item_title: string
  completed: IDrillProgressUserInfo[]
  no_started: IDrillProgressUserInfo[]
  ongoing: IDrillProgressUserInfo[]
  item_completed_count: number
  item_no_started_count: number
  item_ongoing_count: number
}

export interface ICourseLiveStatsSummary {
  own_class_count: number
  student_count: number
  entered_student_count: number
  not_entered_student_count: number
  completed_count: number
  uncompleted_count: number
  ongoing_count: number
  no_started_count: number
  items_list: ICourseLiveDrillProgressInfo[]
}

export type IQueryCourseLiveStudent = IQueryPagination & { entered: boolean | null; current_date: Date }

export interface ICourseLiveParticipateForm {
  title: string
  date: Date
  end_date: Date
  class_ids: number[]
}

export interface IQueryCourse {
  limit: number
  offset: number
}

export interface ICourseResourceTagOption {
  id: string
  tag_name: string
  tag_group: string
}

export interface ICourseResourceGradeTag {
  幼儿园: string
  小学: string
  初中: string
  高中: string
  大学: string
  通用: string
}

export const ICourseResourceGradeTagClassMap: ICourseResourceGradeTag = {
  幼儿园: 'kid',
  小学: 'primary',
  初中: 'junior',
  高中: 'senior',
  大学: 'university',
  通用: 'general',
}

export interface ICourseLiveComment {
  comments_id: string
  comments_time: Date
  comments: string
}

export interface ICourseLiveUserDetail {
  drill_id: string
  live_rank_id: string
  record_id: string | null
  video_url: string | null
  item_title: string
  completion_status: string
  score: number
  score_count: number
  play_count: number
  remark: string
  completion_time: Date
  energy: number
  like: boolean
  score_duration: number
  comments: ICourseLiveComment[]
}

export interface ICourseCommentTemplate {
  txt: string
  state: 0 | 1
}

// 新在线课
export interface IOnlineCourseTag {
  tag_id: string
  tag_name: string
}

export interface IOnlineCourseRepo {
  repository_id: string
  name: string
  category: string
  description: string
  difficult_level: string
  have_homework: boolean
  img_url: string
  tag_list: string[]
  repo_item_id_list: string[]
  online_course_count: number
  course_cycle: 1 | 7 | null
}

export interface ICourseRepoDetail extends IOnlineCourseRepo {
  repo_item_list: ICourseRepoDetailItem[]
  tpl_serial_id?: string
}

export interface ICourseRepoDetailItem {
  course_item_id?: string
  repo_item_id: string
  title: string
  duration: number
  energy: number
  drill_number: number
  video_url: string
  difficult_level: string
  description: string
  repo_item_drills: ICourseRepoDetailItemDrill[]
  current_datetime?: string
  homework_template_id: string | null
}

export interface ICourseRepoDetailItemDrill {
  id: string
  drill_id: string
  drill_type: string
  drill_title: string
  phone_style: string
  exercise_strength: string
}

export interface ICreateCourseForm {
  repository_id: string | null
  repo_item_id?: string
  title: string
  start_datetime: Date
  course_cycle: number | null
  week_no: number | null
  class_id_list: number[]
  course?: ICourseRepoDetail
  modified_repo_item_list?: {
    repo_item_id: string
    current_datetime: string
  }[]
}

export type ICourseResourcePreview = {
  repository_id: string
  title: string
  start_datetime: string
  course_cycle: number
  week_no: number
  repo_item_list: ICourseResourcePreviewRepoItem[]
  class_id_list: number[]
}

export type ICourseResourcePreviewRepoItem = {
  repo_item_id: string
  title: string
  duration: number
  energy: number
  drill_number: number
  video_url: string
  difficult_level: string
  description: string
  repo_item_drills: ICourseRepoDetailItemDrill[]
  current_datetime: string
  course_item_id?: string
  homework_template_id: string | null
}

export interface IQueryScheduleForm {
  name: string | null
  current_datetime: Date | null
  class_id: number | null
}

export interface ICourseScheduleItem {
  online_course_id: string
  online_course_name: string
  item_id: string
  item_title: string
  current_datetime: string
  old_course: boolean

  duration: number
  energy: number
  drill_number: string

  class_ids: number[]
  class_infos: Clazz[]
  teacher_user: ITeacherCreateForm
  teacher_user_id: string
}

export interface ICourseSummaryItem {
  item_id: string
  item_title: string
  current_datetime: string
  duration: number
  energy: number
  drill_number: number
  completion_rate: number
}

export interface IStudentCourseStatus {
  student_vo: IStudentCourseStatusStudentInfo
  class_vo: IStudentCourseStatusClazzInfo
  completion_status: string
}
export interface IStudentCourseStatusStudentInfo {
  user_id: string
  user_no: number
  gender: string
  nick_name?: any
  full_name?: any
  head_img_url?: any
  verified?: any
  student_no?: any
  note_name?: any
}
export interface IStudentCourseStatusClazzInfo {
  id: number
  name: string
  grade_id: number
  grade_no: number
  school_id: number
  class_no: number
  locked: boolean
  need_key: boolean
  school?: any
  note_name?: any
}

export interface ILiveCourseDetail {
  repository_id: string
  name: string
  course_cycle: number
  category: string
  description: string
  difficult_level: string
  have_homework: boolean
  img_url: string | null
  tag_list: string[]
  repo_item_id_list: string[]
  online_course_count: number
  online_course_item_list: ICourseRepoDetailItem[]
  repo_item_list?: ICourseRepoDetailItem[]
  online_course_title: string
  online_course_id: string
  start_datetime: string
  end_datetime: string
  class_vo_list: Clazz[]
  tpl_serial_id: string
  homework_id: string
}

export interface IStudentLessonFinishDetail {
  drill_id: string
  live_rank_id: string
  record_id: string
  drill_title: string
  completion_status: string
  video_url: string
  current_datetime: string
}

export interface IOnlineCourseClassSummary {
  students_count: number
  completion_count: number
  uncompleted_count: number
  completion_rate: number
  class_vo: Clazz
}

export interface ILiveCourseDrillReport {
  drill_id: string
  record_id: string
  record_time: string
  duration: number
  score: number
  score_count: number
  score_duration: number
  score_grade: number
  score_rate: number
  success_count: number
  fail_count: number
  interrupt_count: number
  max_combo_count: number
  video_url: string
  burned_calories: number
}

export interface ILiveCourseRecord {
  drill_id: string
  live_rank_id: string
  drill_title: string
  completion_status: string
  score?: any
  score_duration?: any
  score_count?: any
  score_grade?: any
  record_id?: any
  video_url?: any
  current_datetime?: string
}
